/**
 * Declenche les sons a partir des transitions d'etat du store. Centralise ici
 * plutot que disperse dans les ecrans : un seul endroit sait quand ca sonne.
 */
import { useEffect, useRef } from 'react';
import { COUNTDOWN_TIME } from '@retusch/shared';
import { useRoomStore } from '../store/roomStore.js';
import { useUiStore } from '../store/uiStore.js';
import { playSound, setMuted, unlockAudio } from './sounds.js';

/** Deverrouille l'audio au premier geste, comme l'exigent les navigateurs. */
export function useAudioUnlock(): void {
  useEffect(() => {
    const unlock = (): void => unlockAudio();
    const events = ['pointerdown', 'keydown'] as const;
    for (const event of events) window.addEventListener(event, unlock, { once: true });
    return () => {
      for (const event of events) window.removeEventListener(event, unlock);
    };
  }, []);
}

export function useGameSounds(): void {
  const muted = useUiStore((state) => state.muted);
  const room = useRoomStore((state) => state.room);
  const guessResult = useRoomStore((state) => state.guessResult);
  const submittedRound = useRoomStore((state) => state.submittedRound);

  useEffect(() => setMuted(muted), [muted]);

  // Arrivee d'un joueur, uniquement en lobby : pendant la partie ce serait du
  // bruit a chaque reconnexion.
  const previousCount = useRef<number | null>(null);
  useEffect(() => {
    const count = room?.players.length ?? null;
    if (room?.phase === 'LOBBY' && previousCount.current !== null && count !== null) {
      if (count > previousCount.current) playSound('join');
    }
    previousCount.current = count;
  }, [room?.players.length, room?.phase]);

  // Envoi du dessin.
  const previousSubmitted = useRef(0);
  useEffect(() => {
    if (submittedRound > 0 && submittedRound !== previousSubmitted.current) playSound('submit');
    previousSubmitted.current = submittedRound;
  }, [submittedRound]);

  // Resultat de la devinette.
  const resultFired = useRef(false);
  useEffect(() => {
    if (guessResult === null) {
      resultFired.current = false;
      return;
    }
    if (resultFired.current) return;
    resultFired.current = true;
    playSound(guessResult.found ? 'found' : 'missed');
  }, [guessResult]);

  // Podium.
  const previousPhase = useRef<string | null>(null);
  useEffect(() => {
    const phase = room?.phase ?? null;
    if (phase === 'GAME_END' && previousPhase.current !== 'GAME_END') playSound('podium');
    previousPhase.current = phase;
  }, [room?.phase]);

  // Ticks du decompte : un par seconde, cales sur l'heure serveur via endsAt.
  const endsAt = room?.endsAt ?? null;
  const isCountdown = room?.phase === 'COUNTDOWN';
  useEffect(() => {
    if (!isCountdown || endsAt === null) return;

    const timers: number[] = [];
    for (let second = COUNTDOWN_TIME; second >= 1; second -= 1) {
      const delay = endsAt - Date.now() - (second - 1) * 1000;
      if (delay > 0) timers.push(window.setTimeout(() => playSound('tick'), delay));
    }
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [isCountdown, endsAt]);
}
